import { useCallback, useState } from 'react';
import { ApiRefusal } from '@gatecheckpro/api-client';
import type { VenueMapTopology, VenueMapVersionTopology } from '@gatecheckpro/contracts';
import { api } from '../api.ts';
import { roomTopologyOf } from '@gatecheckpro/venue-map';

/**
 * The editor's working document, and how honest it is about where it lives.
 *
 * Local unsaved state is allowed and expected — an editor that round-tripped
 * every keystroke would be unusable at a venue on a bad connection. What is not
 * allowed is letting the two be confused. The server draft is authority; what
 * is in this hook is a proposal, and the surface has to say which one the
 * person is looking at.
 *
 * Five states, because five different things are true and each needs a
 * different next move:
 *
 *   - `persisted` — what is on screen is what the server has.
 *   - `unsaved`   — edits exist here and nowhere else.
 *   - `saving`    — in flight; the outcome is not known yet.
 *   - `failed`    — the save was refused, and the edits are still here.
 *   - `stale`     — someone else saved first. The edits are still here, and
 *                   they will not be applied on top of work this editor has
 *                   never seen.
 *
 * Nothing merges. A whole-document import that silently combined two people's
 * rooms would produce a seating plan neither of them drew, and the failure
 * would be discovered by an attendee standing in front of a chair that does not
 * exist. The stale case is a decision for a person, not for this hook.
 */
export type DraftStatus = 'persisted' | 'unsaved' | 'saving' | 'failed' | 'stale';

export interface TopologyDraft {
  readonly document: VenueMapTopology;
  readonly status: DraftStatus;
  /** The digest the next save will be conditional on. */
  readonly expectedDigest: string;
  readonly error: unknown;
  /** The version now on the server, once a save has been refused as stale. */
  readonly serverVersion: VenueMapVersionTopology | null;
  edit: (change: (current: VenueMapTopology) => VenueMapTopology) => void;
  save: () => Promise<boolean>;
  /** Throws away local edits and takes the server's version. Never automatic. */
  discardAndReload: () => Promise<void>;
}

export function useTopologyDraft(
  base: VenueMapVersionTopology,
  versionPath: string,
): TopologyDraft {
  const [document, setDocument] = useState<VenueMapTopology>(() => roomTopologyOf(base));
  const [expectedDigest, setExpectedDigest] = useState(base.version.topologyDigest);
  const [status, setStatus] = useState<DraftStatus>('persisted');
  const [error, setError] = useState<unknown>(null);
  const [serverVersion, setServerVersion] = useState<VenueMapVersionTopology | null>(null);

  const edit = useCallback((change: (current: VenueMapTopology) => VenueMapTopology) => {
    setDocument((current) => change(current));
    // An edit after a stale refusal keeps the stale state: the conflict has not
    // gone away because somebody typed over it, and the next save must still
    // refuse rather than overwrite.
    setStatus((current) => (current === 'stale' ? 'stale' : 'unsaved'));
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    setStatus('saving');
    setError(null);
    try {
      // The digest travels as `If-Match`, which is where the server reads it
      // from. It is a precondition on the write, not a field of the room.
      const saved = await api.request<VenueMapVersionTopology>(`${versionPath}/topology`, {
        method: 'PUT',
        body: { topology: document },
        ifMatch: expectedDigest,
      });
      setDocument(roomTopologyOf(saved));
      setExpectedDigest(saved.version.topologyDigest);
      setServerVersion(null);
      setStatus('persisted');
      return true;
    } catch (caught) {
      setError(caught);
      const stale = caught instanceof ApiRefusal && caught.code === 'VENUE_MAP_TOPOLOGY_STALE';
      if (stale) {
        // Fetch what actually landed, so the surface can say what changed
        // rather than only that something did. Read-only: nothing here decides
        // what to keep.
        try {
          setServerVersion(await api.request<VenueMapVersionTopology>(versionPath));
        } catch {
          setServerVersion(null);
        }
      }
      setStatus(stale ? 'stale' : 'failed');
      return false;
    }
  }, [document, expectedDigest, versionPath]);

  const discardAndReload = useCallback(async () => {
    const fresh = await api.request<VenueMapVersionTopology>(versionPath);
    setDocument(roomTopologyOf(fresh));
    setExpectedDigest(fresh.version.topologyDigest);
    setServerVersion(null);
    setError(null);
    setStatus('persisted');
  }, [versionPath]);

  return { document, status, expectedDigest, error, serverVersion, edit, save, discardAndReload };
}
